import { ComponentProps, CSSProperties, useEffect, useMemo } from "react";
import { Rail, Handles, Ticks, Tracks, Slider } from "react-compound-slider";
import { Handle } from "./Handle";
import { Tick } from "./Tick";
import { Track } from "./Track";

interface SliderProps {
  listOptions: object;
  rain_initial: number;
  setRainInitial: (value: number) => void;
  dry_initial: number;
  setDryInitial: (value: number) => void;
  isDisabled?: boolean;
}

const sliderStyle = {
  position: "relative",
  width: "100%",
} as CSSProperties;

// The slider exposes handle identities through Tracks. Notify after commit:
// its numeric onUpdate values are sorted and do not identify rain versus dry.
function SeasonTrack({
  source,
  target,
  rain_initial,
  dry_initial,
  setRainInitial,
  setDryInitial,
  ...trackProps
}: ComponentProps<typeof Track> & Pick<SliderProps, "rain_initial" | "dry_initial" | "setRainInitial" | "setDryInitial">) {
  const rain = source.id === "$$-0" ? source.value : target.value;
  const dry = source.id === "$$-1" ? source.value : target.value;

  useEffect(() => {
    if (trackProps.disable) return;
    if (rain !== rain_initial) setRainInitial(rain);
    if (dry !== dry_initial) setDryInitial(dry);
  }, [rain, dry, rain_initial, dry_initial, setRainInitial, setDryInitial, trackProps.disable]);

  return <Track {...trackProps} source={source} target={target} />;
}

const domain = [0, 11];

export const SliderComponent = ({
  listOptions,
  rain_initial,
  dry_initial,
  setRainInitial,
  setDryInitial,
  isDisabled = false,
}: SliderProps) => {
  const boolSecaAntesDaChuva = dry_initial < rain_initial;
  const values = useMemo(() => [rain_initial, dry_initial], [rain_initial, dry_initial]);
  const valMap: any = listOptions;

  return (
    <Slider
      mode={1}
      step={1}
      domain={domain}
      rootStyle={sliderStyle}
      values={values}
      disabled={isDisabled}
    >
      {/* Tracejado na barra indicando os meses */}
      <Rail>
        {({ getRailProps }) => (
          <div
            className={boolSecaAntesDaChuva ? "lineBlue" : "lineGray"}
            style={{
              cursor: isDisabled ? "default" : "pointer",
            }}
            {...(!isDisabled && getRailProps())}
          />
        )}
      </Rail>
      {/* Marcação do inicio da chuva e inico da seca */}
      <Handles>
        {({ handles, getHandleProps }) => (
          <div className="slider-handles">
            {handles.map((handle) => (
              <Handle
                key={handle.id}
                handle={handle}
                domain={domain}
                getHandleProps={getHandleProps}
                disable={isDisabled}
              />
            ))}
          </div>
        )}
      </Handles>
      {/* Define a cor da barra indicando onde começa e termina a chuva e a seca respectivamente*/}
      <Tracks right={false} left={false}>
        {({ tracks, getTrackProps }) => (
          <div className="slider-tracks">
            {tracks.map(({ id, source, target }) => (
              <SeasonTrack
                key={id}
                source={source}
                target={target}
                rain_initial={rain_initial}
                dry_initial={dry_initial}
                setRainInitial={setRainInitial}
                setDryInitial={setDryInitial}
                getTrackProps={getTrackProps}
                boolColor={boolSecaAntesDaChuva}
                disable={isDisabled}
              />
            ))}
          </div>
        )}
      </Tracks>
      {/* Demarcação dos meses na barra */}
      <Ticks count={11}>
        {({ ticks }) => (
          <div className="slider-ticks">
            {ticks.map((tick) => (
              <Tick
                key={tick.id}
                format={(id: number) => valMap[id]}
                tick={tick}
                count={ticks.length}
                disable={isDisabled}
              />
            ))}
          </div>
        )}
      </Ticks>
    </Slider>
  );
};
